import { api } from '@/shared/api/client';
import { loadConfig } from '@/shared/api/config';

// ─── types ───────────────────────────────────────────────────────────────

export interface CatalogListItem {
  Id: string;
  Name: string;
  Description?: string | null;
  TemplateId: number;
  ItemCount: number;
  ShowPrice: boolean;
  AllowOrdering: boolean;
  ExpiresOn?: string | null;
  Status: number;
  StatusLabel: string;
  IsExpired: boolean;
  HasShareLink: boolean;
  ShareToken?: string | null;
  PublishedOn?: string | null;
}

export interface CatalogItem {
  Id: string;
  FKItemId: string;
  ItemCode: string;
  ItemName: string;
  SortOrder: number;
  PriceOverride?: number | null;
  ResolvedPrice: number;
  PrimaryImageId?: string | null;
  PrimaryImageUrl?: string | null;
}

export interface CatalogDetail {
  Id: string;
  Name: string;
  Description?: string | null;
  TemplateId: number;
  ShowPrice: boolean;
  AllowOrdering: boolean;
  PriceGroupId?: string | null;
  PriceGroupName?: string | null;
  ExpiresOn?: string | null;
  Status: number;
  StatusLabel: string;
  IsExpired: boolean;
  ShareToken?: string | null;
  PublishedOn?: string | null;
  NotifyOnOrder: boolean;
  NotifyEmails: string[];
  Items: CatalogItem[];
}

export interface SaveCatalogItemRequest {
  FKItemId: string;
  SortOrder: number;
  PriceOverride?: number | null;
}

export interface SaveCatalogRequest {
  Name: string;
  Description?: string | null;
  TemplateId: number;
  ShowPrice: boolean;
  AllowOrdering: boolean;
  PriceGroupId?: string | null;
  ExpiresOn?: string | null;
  NotifyOnOrder: boolean;
  NotifyEmails: string[];
  Items: SaveCatalogItemRequest[];
}

export interface CatalogOrderLine {
  Id: string;
  FKItemId: string;
  ItemCode: string;
  ItemName: string;
  Qty: number;
  UnitPrice: number;
  LineTotal: number;
}

export interface CatalogOrder {
  Id: string;
  OrderNo: string;
  CatalogId: string;
  CatalogName: string;
  CustomerName: string;
  CustomerPhone: string;
  PhoneVerified: boolean;
  CustomerEmail?: string | null;
  DeliveryAddress?: string | null;
  CustomerNote?: string | null;
  TotalQty: number;
  TotalAmount: number;
  Status: number;
  StatusLabel: string;
  FKCustomerId?: string | null;
  SubmittedOn?: string | null;
  ConfirmedOn?: string | null;
  RejectReason?: string | null;
  /** Эх сурвалж: 0 = холбоос (нийтийн каталог), 1 = апп (онлайн дэлгүүр). Backend нэмэгдэх хүртэл сонголттой. */
  Source?: number;
  SourceLabel?: string;
  /** Апп-аас харилцагч өөрөө цуцалсан огноо (Status = 4). */
  CancelledOn?: string | null;
  /** Хүргүүлэхийг хүссэн өдөр (зөвхөн огноо). */
  RequestedDeliveryDate?: string | null;
  /** Хүргэлтийн салбар (апп-аас сонгосон). */
  BranchName?: string | null;
  /** Апп-аар захиалга өгсөн хэрэглэгчийн нэр. */
  PlacedByName?: string | null;
  Lines: CatalogOrderLine[];
}

export interface ConvertResult {
  SalesOrderId: string;
  SalesOrderNo: string;
}

// ─── public ──────────────────────────────────────────────────────────────

export interface PublicCatalogItem {
  ItemId: string;
  Code: string;
  Name: string;
  CategoryName?: string | null;
  Price?: number | null;
  /** Бөөний борлуулалт (зөвхөн ShowPrice үед утгатай). */
  IsWholeSales?: boolean;
  WholeQty?: number;
  WholePrice?: number;
  ImageId?: string | null;
  ImageUrl?: string | null;
  /** Бэлэн болгож тооцоолсон зургийн бүтэн URL (upload эсвэл гадаад). */
  ResolvedImageUrl?: string | null;
}

/**
 * Захиалсан тооноос хамаарсан нэгж үнэ. Тоо >= бөөний тоо үед бөөний үнэ.
 * Backend ResolvePrice-ийн дүрэмтэй ижил (бөөний үнэ давамгайлна).
 */
export function effectiveCatalogPrice(item: PublicCatalogItem, qty: number): number {
  const base = item.Price ?? 0;
  if (item.IsWholeSales && (item.WholeQty ?? 0) > 0 && (item.WholePrice ?? 0) > 0 && qty >= (item.WholeQty as number)) {
    return item.WholePrice as number;
  }
  return base;
}

export interface PublicCatalog {
  Name: string;
  Description?: string | null;
  TemplateId: number;
  ShowPrice: boolean;
  AllowOrdering: boolean;
  IsExpired: boolean;
  Items: PublicCatalogItem[];
}

export interface SubmitOrderRequest {
  SmsSessionId: string;
  CustomerName: string;
  CustomerPhone: string;
  CustomerEmail?: string | undefined;
  DeliveryAddress?: string | undefined;
  CustomerNote?: string | undefined;
  Lines: { ItemId: string; Qty: number }[];
}

export interface SubmitOrderResult {
  OrderId: string;
  OrderNo: string;
}

export interface SmsStartResult {
  SessionId: string;
  DisplayInstruction: string;
  SmsUri: string;
  ExpiresAt: string;
}

export interface SmsStatusResult {
  SessionId: string;
  SessionStatus: string;
  ExpiresAt: string;
}

// ─── admin (authenticated) ─────────────────────────────────────────────────

export const listCatalogs = () => api.get<CatalogListItem[]>('/api/catalog');
export const getCatalog = (id: string) => api.get<CatalogDetail>(`/api/catalog/${id}`);
export const createCatalog = (req: SaveCatalogRequest) =>
  api.post<CatalogDetail>('/api/catalog', req);
export const updateCatalog = (id: string, req: SaveCatalogRequest) =>
  api.put<CatalogDetail>(`/api/catalog/${id}`, req);
export const deleteCatalog = (id: string) => api.del<void>(`/api/catalog/${id}`);
export const publishCatalog = (id: string) =>
  api.post<CatalogDetail>(`/api/catalog/${id}/publish`);
export const resetCatalogToken = (id: string) =>
  api.post<CatalogDetail>(`/api/catalog/${id}/reset-token`);

export const listCatalogOrders = (catalogId?: string) =>
  api.get<CatalogOrder[]>('/api/catalog/orders' + (catalogId ? `?catalogId=${catalogId}` : ''));
export const countNewCatalogOrders = () =>
  api.get<{ Count: number }>('/api/catalog/orders/new-count');
export const getCatalogOrder = (id: string) =>
  api.get<CatalogOrder>(`/api/catalog/orders/${id}`);
export const confirmCatalogOrder = (id: string) =>
  api.post<CatalogOrder>(`/api/catalog/orders/${id}/confirm`);
export const rejectCatalogOrder = (id: string, reason: string) =>
  api.post<CatalogOrder>(`/api/catalog/orders/${id}/reject`, { Reason: reason });
export const convertCatalogOrder = (id: string) =>
  api.post<ConvertResult>(`/api/catalog/orders/${id}/convert`);

// ─── public (unauthenticated) ──────────────────────────────────────────────

async function publicFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const cfg = await loadConfig();
  const res = await fetch(cfg.apiBaseUrl + path, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    if (parsed && typeof parsed === 'object') {
      const p = parsed as Record<string, unknown>;
      if (typeof p.detail === 'string' && p.detail) message = p.detail;
      else if (typeof p.error === 'string' && p.error) message = p.error;
      else if (typeof p.title === 'string' && p.title) message = p.title;
    } else if (typeof parsed === 'string' && parsed) {
      message = parsed;
    }
    throw new Error(message);
  }
  return parsed as T;
}

export async function fetchPublicCatalog(token: string): Promise<PublicCatalog> {
  const cfg = await loadConfig();
  const data = await publicFetch<PublicCatalog>(
    `/api/public/catalog/${encodeURIComponent(token)}`,
  );
  for (const it of data.Items) {
    it.ResolvedImageUrl = it.ImageUrl
      ? it.ImageUrl
      : it.ImageId
        ? `${cfg.apiBaseUrl}/api/public/catalog/${encodeURIComponent(token)}/item/${it.ItemId}/image/${it.ImageId}`
        : null;
  }
  return data;
}

export const submitPublicOrder = (token: string, req: SubmitOrderRequest) =>
  publicFetch<SubmitOrderResult>(`/api/public/catalog/${encodeURIComponent(token)}/order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });

export const startSmsOtp = (phone: string) =>
  publicFetch<SmsStartResult>('/api/sms-otp/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ Phone: phone }),
  });

export const getSmsStatus = (sessionId: string) =>
  publicFetch<SmsStatusResult>(`/api/sms-otp/status/${encodeURIComponent(sessionId)}`);

// ─── public: миний захиалгууд (OTP-баталгаатай) ────────────────────────────

export interface MyOrderLine {
  Name: string;
  Qty: number;
  UnitPrice: number;
  LineTotal: number;
}

export interface MyCatalogOrder {
  OrderNo: string;
  SubmittedOn: string;
  Status: number;
  StatusLabel: string;
  TotalQty: number;
  TotalAmount: number;
  CatalogName?: string | null;
  RejectReason?: string | null;
  Lines: MyOrderLine[];
}

export const myCatalogOrders = (
  token: string,
  req: { SmsSessionId: string; CustomerPhone: string },
) =>
  publicFetch<MyCatalogOrder[]>(`/api/public/catalog/${encodeURIComponent(token)}/my-orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
